import { useState } from "react";

import { MedicamentoCombobox } from "@/components/MedicamentoCombobox";
import { RoleGate } from "@/components/RoleGate";
import { Scanner } from "@/components/Scanner";
import {
  lookupLotePorEtiqueta,
  lookupMedicamentoPorEan,
  useEditarMedicamento,
  useEntradaManual,
  useEstornarMovimentacao,
  useLocais,
  useLotesEmEstoque,
  useMedicamentos,
  useMovimentacoesManuais,
  useSaidaManual,
  type MovimentacaoManualRecente,
} from "@/hooks/useApi";
import type { Medicamento } from "@/types/api";

const MOTIVOS_ENTRADA = [
  { value: "saldo_abertura", label: "Saldo de abertura (inventário inicial)" },
  { value: "ajuste", label: "Ajuste manual" },
  { value: "doacao", label: "Doação" },
  { value: "outro", label: "Outro" },
];

const MOTIVOS_SAIDA = [
  { value: "perda", label: "Perda" },
  { value: "avaria", label: "Avaria / quebra" },
  { value: "vencimento", label: "Vencimento" },
  { value: "descarte", label: "Descarte" },
  { value: "ajuste_inventario", label: "Ajuste de inventário" },
  { value: "outro", label: "Outro" },
];

const MOTIVO_LABEL: Record<string, string> = {
  ...Object.fromEntries([...MOTIVOS_ENTRADA, ...MOTIVOS_SAIDA].map((m) => [m.value, m.label])),
  estorno: "Estorno",
};

function fmtDateTime(s: string): string {
  return new Date(s).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

/** EAN plausível: só dígitos, 8–14 posições (EAN-8/12/13/14). Descarta
 * DataMatrix/QR de etiqueta interna, que não devem virar EAN de catálogo. */
function pareceEan(codigo: string): boolean {
  return /^\d{8,14}$/.test(codigo);
}

/** Aprender EAN no 1º scan: código lido sem dono + medicamento selecionado →
 * um toque vincula no catálogo (PATCH auditado; papel farmácia já pode). */
function VincularEan({ ean, med, onDone }: {
  ean: string;
  med: Medicamento | undefined;
  onDone: (msg: string) => void;
}) {
  const editar = useEditarMedicamento();

  if (!med) {
    return (
      <p className="mt-1 text-xs text-amber-700">
        📷 Código <b>{ean}</b> ainda não vinculado a nenhum medicamento — selecione o
        medicamento acima e toque em <b>Vincular</b> pra ensinar o catálogo.
      </p>
    );
  }

  const substituindo = med.codigo_barras_ean && med.codigo_barras_ean !== ean;

  async function vincular() {
    try {
      await editar.mutateAsync({ id: med!.id, patch: { codigo_barras_ean: ean } });
      onDone(`✅ EAN ${ean} vinculado a ${med!.descricao}. Próximos scans resolvem sozinhos.`);
    } catch { /* erro via editar.error */ }
  }

  return (
    <div className="mt-1 space-y-1">
      <button type="button" className="btn-secondary px-2 py-1 text-xs" disabled={editar.isPending} onClick={vincular}>
        {editar.isPending
          ? "Vinculando…"
          : substituindo
            ? `🔗 Substituir EAN atual (${med.codigo_barras_ean}) por ${ean}`
            : `🔗 Vincular EAN ${ean} a ${med.codigo_interno}`}
      </button>
      {substituindo ? (
        <p className="text-xs text-amber-700">
          ⚠️ Este medicamento já tem outro EAN cadastrado — confirme na embalagem antes de substituir.
        </p>
      ) : null}
      {editar.error ? (
        <p className="text-xs text-anarosa">{(editar.error as Error).message}</p>
      ) : null}
    </div>
  );
}

function fmtDate(s: string): string {
  return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
}

export function EntradaEstoquePage() {
  return (
    <RoleGate allowed={["admin", "farmaceutico"]}>
      <Inner />
    </RoleGate>
  );
}

function Inner() {
  const [aba, setAba] = useState<"entrada" | "saida">("entrada");
  const [estornando, setEstornando] = useState<MovimentacaoManualRecente | null>(null);
  const { data: recentes } = useMovimentacoesManuais();

  const tabBase = "rounded-t-md px-4 py-2 text-sm font-medium border border-b-0";
  const tabOn = "bg-white text-anarosa border-gray-200";
  const tabOff = "bg-gray-50 text-ink-muted border-transparent hover:text-ink";

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-semibold">Movimentações de Estoque (sem NF)</h2>
        <p className="text-sm text-ink-muted">
          <b>Entrada</b>: saldo de abertura e ajustes. <b>Saída</b>: baixa justificada por
          perda, avaria, vencimento ou descarte. Controlados entram/saem do livro automaticamente.
        </p>
      </div>

      <div>
        <div className="flex gap-1">
          <button className={`${tabBase} ${aba === "entrada" ? tabOn : tabOff}`} onClick={() => setAba("entrada")}>
            ➕ Entrada
          </button>
          <button className={`${tabBase} ${aba === "saida" ? tabOn : tabOff}`} onClick={() => setAba("saida")}>
            ➖ Saída justificada
          </button>
        </div>
        <div className="card rounded-tl-none p-4">
          {aba === "entrada" ? <FormEntrada /> : <FormSaida />}
        </div>
      </div>

      {recentes && recentes.length > 0 ? (
        <div>
          <h3 className="font-display text-base font-semibold mb-2">Lançamentos recentes</h3>
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="px-3 py-2">Quando</th>
                  <th className="px-3 py-2">Tipo</th>
                  <th className="px-3 py-2">Medicamento</th>
                  <th className="px-3 py-2">Local</th>
                  <th className="px-3 py-2">Lote</th>
                  <th className="px-3 py-2 text-right">Qtd</th>
                  <th className="px-3 py-2">Motivo</th>
                  <th className="px-3 py-2">Por</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentes.map((r) => (
                  <tr key={r.id} className={r.estornado ? "opacity-50" : undefined}>
                    <td className="px-3 py-1.5 text-ink-muted whitespace-nowrap">{fmtDateTime(r.ts)}</td>
                    <td className="px-3 py-1.5">
                      {r.tipo === "entrada" ? (
                        <span className="badge bg-green-100 text-green-800">Entrada</span>
                      ) : r.tipo === "saida" ? (
                        <span className="badge bg-red-100 text-red-800">Saída</span>
                      ) : (
                        <span className="badge bg-gray-200 text-gray-700">↩ Estorno</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5"><code className="text-xs">{r.medicamento_codigo}</code> {r.medicamento_descricao}</td>
                    <td className="px-3 py-1.5 text-ink-muted">{r.local}</td>
                    <td className="px-3 py-1.5 text-xs">{r.numero_lote}</td>
                    <td className="px-3 py-1.5 text-right font-medium">{r.quantidade}</td>
                    <td className="px-3 py-1.5 text-xs text-ink-muted" title={r.observacao ?? undefined}>
                      {MOTIVO_LABEL[r.motivo] ?? r.motivo}
                    </td>
                    <td className="px-3 py-1.5 text-xs text-ink-muted">{r.usuario ?? "—"}</td>
                    <td className="px-3 py-1.5 text-right">
                      {r.tipo !== "estorno" ? (
                        r.estornado ? (
                          <span className="badge bg-gray-200 text-gray-600">Estornado</span>
                        ) : (
                          <button
                            className="btn-secondary px-2 py-1 text-xs whitespace-nowrap"
                            onClick={() => setEstornando(r)}
                            title="Estornar este lançamento (contra-lançamento com justificativa)"
                          >
                            ↩ Estornar
                          </button>
                        )
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {estornando ? (
        <ModalEstorno mov={estornando} onClose={() => setEstornando(null)} />
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------ Estorno

function ModalEstorno({ mov, onClose }: { mov: MovimentacaoManualRecente; onClose: () => void }) {
  const estornar = useEstornarMovimentacao();
  const [just, setJust] = useState("");
  const ok = just.trim().length >= 5;

  async function confirmar() {
    if (!ok) return;
    try {
      await estornar.mutateAsync({ audit_id: mov.id, justificativa: just.trim() });
      onClose();
    } catch { /* erro via estornar.error */ }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="card w-full max-w-md p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="font-display text-lg font-semibold">Estornar lançamento</h3>
          <button className="text-ink-muted hover:text-ink" onClick={onClose}>✕</button>
        </div>

        <div className="rounded bg-gray-50 px-3 py-2 text-sm">
          <p>
            <span className={`badge mr-1 ${mov.tipo === "entrada" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
              {mov.tipo === "entrada" ? "Entrada" : "Saída"}
            </span>
            <code className="text-xs">{mov.medicamento_codigo}</code> {mov.medicamento_descricao}
          </p>
          <p className="mt-1 text-xs text-ink-muted">
            {mov.local} · lote {mov.numero_lote} · qtd <b>{mov.quantidade}</b> · {fmtDateTime(mov.ts)}
          </p>
        </div>

        <p className="text-xs text-ink-muted">
          O estorno <b>não apaga</b> o lançamento: cria um contra-lançamento amarrado a ele
          (estoque{mov.tipo === "entrada" ? " −" : " +"}{mov.quantidade}; controlado também
          ajusta o livro). Depois, se precisar, lance o valor correto normalmente.
        </p>

        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">
            Justificativa <span className="text-anarosa">(obrigatória)</span>
          </label>
          <input
            className="input mt-1"
            placeholder="ex.: lançamento duplicado; quantidade digitada errada"
            value={just}
            onChange={(e) => setJust(e.target.value)}
            autoFocus
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" disabled={!ok || estornar.isPending} onClick={confirmar}>
            {estornar.isPending ? "Estornando…" : "Confirmar estorno"}
          </button>
        </div>
        {estornar.error ? (
          <p className="text-sm text-anarosa">{(estornar.error as Error).message}</p>
        ) : null}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Entrada

function FormEntrada() {
  const { data: locais } = useLocais();
  const { data: medicamentos } = useMedicamentos({});
  const criar = useEntradaManual();

  const [medId, setMedId] = useState("");
  const [localId, setLocalId] = useState("");
  const [lote, setLote] = useState("");
  const [validade, setValidade] = useState("");
  const [fabricacao, setFabricacao] = useState("");
  const [qtd, setQtd] = useState("");
  const [motivo, setMotivo] = useState("saldo_abertura");
  const [obs, setObs] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanMsg, setScanMsg] = useState<string | null>(null);
  const [eanPendente, setEanPendente] = useState<string | null>(null);

  const med = medicamentos?.find((m) => m.id === medId);

  async function handleScan(codigo: string) {
    setScannerOpen(false);
    setScanMsg(null);
    setEanPendente(null);
    try {
      const m = await lookupMedicamentoPorEan(codigo);
      setMedId(m.id);
    } catch {
      if (pareceEan(codigo)) {
        setEanPendente(codigo); // oferece vincular ao medicamento selecionado
      } else {
        setScanMsg(`Código "${codigo}" não é um EAN válido nem etiqueta conhecida.`);
      }
    }
  }

  async function submit() {
    if (!medId || !localId || !lote || !validade || Number(qtd) <= 0) return;
    try {
      await criar.mutateAsync({
        medicamento_id: medId,
        local_id: localId,
        numero_lote: lote,
        data_validade: validade,
        data_fabricacao: fabricacao || null,
        quantidade: Number(qtd),
        motivo,
        observacao: obs || null,
      });
      // mantém medicamento/local/motivo pra lançamentos em sequência
      setLote(""); setValidade(""); setFabricacao(""); setQtd(""); setObs("");
    } catch { /* erro via criar.error */ }
  }

  const podeSalvar = medId && localId && lote && validade && Number(qtd) > 0 && !criar.isPending;

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs uppercase tracking-wider text-ink-muted">Medicamento</label>
        <div className="mt-1 flex gap-2">
          <MedicamentoCombobox
            medicamentos={medicamentos ?? []}
            value={medId}
            onChange={setMedId}
          />
          <button type="button" className="btn-secondary" title="Escanear EAN" onClick={() => setScannerOpen(true)}>📷</button>
        </div>
        {med?.portaria_344 ? (
          <p className="mt-1 text-xs text-amber-700">⚠️ Controlado (Portaria 344) — entrará no livro como saldo inicial.</p>
        ) : null}
        {eanPendente ? (
          <VincularEan
            ean={eanPendente}
            med={med}
            onDone={(msg) => { setEanPendente(null); setScanMsg(msg); }}
          />
        ) : null}
        {scanMsg ? <p className="mt-1 text-xs text-anarosa">{scanMsg}</p> : null}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Local destino</label>
          <select className="input mt-1" value={localId} onChange={(e) => setLocalId(e.target.value)}>
            <option value="">— escolha o local —</option>
            {(locais ?? []).map((l) => <option key={l.id} value={l.id}>{l.descricao}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Motivo</label>
          <select className="input mt-1" value={motivo} onChange={(e) => setMotivo(e.target.value)}>
            {MOTIVOS_ENTRADA.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Lote</label>
          <input className="input mt-1" value={lote} onChange={(e) => setLote(e.target.value)} />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Validade</label>
          <input className="input mt-1" type="date" value={validade} onChange={(e) => setValidade(e.target.value)} />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Fabricação (opc.)</label>
          <input className="input mt-1" type="date" value={fabricacao} onChange={(e) => setFabricacao(e.target.value)} />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Quantidade</label>
          <input className="input mt-1" type="number" min={1} value={qtd} onChange={(e) => setQtd(e.target.value)} />
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-wider text-ink-muted">Observação (opc.)</label>
        <input className="input mt-1" value={obs} onChange={(e) => setObs(e.target.value)} />
      </div>

      <div className="flex items-center gap-3 pt-1">
        <button className="btn-primary" disabled={!podeSalvar} onClick={submit}>
          {criar.isPending ? "Lançando…" : "Lançar entrada"}
        </button>
        {criar.error ? <span className="text-anarosa text-sm">{(criar.error as Error).message}</span> : null}
        {criar.isSuccess ? <span className="text-green-700 text-sm">✅ Lançado. Pode inserir o próximo lote.</span> : null}
      </div>

      <Scanner open={scannerOpen} onScan={handleScan} onClose={() => setScannerOpen(false)} />
    </div>
  );
}

// -------------------------------------------------------------------- Saída

function FormSaida() {
  const { data: locais } = useLocais();
  const { data: medicamentos } = useMedicamentos({});
  const baixar = useSaidaManual();

  const [localId, setLocalId] = useState("");
  const [medId, setMedId] = useState("");
  const [loteId, setLoteId] = useState("");
  const [qtd, setQtd] = useState("");
  const [motivo, setMotivo] = useState("perda");
  const [obs, setObs] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanMsg, setScanMsg] = useState<string | null>(null);
  const [eanPendente, setEanPendente] = useState<string | null>(null);

  const { data: lotes } = useLotesEmEstoque(localId || null, medId || null);
  const lote = lotes?.find((l) => l.lote_id === loteId);
  const med = medicamentos?.find((m) => m.id === medId);

  async function handleScan(codigo: string) {
    setScannerOpen(false);
    setScanMsg(null);
    setEanPendente(null);
    // 1º tenta etiqueta interna (DataMatrix) → lote direto; senão EAN → medicamento
    try {
      const l = await lookupLotePorEtiqueta(codigo);
      setMedId(l.medicamento_id);
      setLoteId(l.lote_id);
      return;
    } catch { /* não é etiqueta interna */ }
    try {
      const m = await lookupMedicamentoPorEan(codigo);
      setMedId(m.id);
      setLoteId("");
    } catch {
      if (pareceEan(codigo)) {
        setEanPendente(codigo); // oferece vincular ao medicamento selecionado
      } else {
        setScanMsg(`Código "${codigo}" não encontrado (nem etiqueta interna, nem EAN).`);
      }
    }
  }

  async function submit() {
    if (!loteId || !localId || Number(qtd) <= 0 || obs.trim().length < 5) return;
    try {
      await baixar.mutateAsync({
        lote_id: loteId,
        local_id: localId,
        quantidade: Number(qtd),
        motivo,
        observacao: obs.trim(),
      });
      // mantém local/motivo; limpa item pra próxima baixa
      setMedId(""); setLoteId(""); setQtd(""); setObs("");
    } catch { /* erro via baixar.error */ }
  }

  const obsCurta = obs.trim().length > 0 && obs.trim().length < 5;
  const podeSalvar =
    loteId && localId && Number(qtd) > 0 && obs.trim().length >= 5 && !baixar.isPending;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Local de origem</label>
          <select
            className="input mt-1"
            value={localId}
            onChange={(e) => { setLocalId(e.target.value); setLoteId(""); }}
          >
            <option value="">— escolha o local —</option>
            {(locais ?? []).map((l) => <option key={l.id} value={l.id}>{l.descricao}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Motivo</label>
          <select className="input mt-1" value={motivo} onChange={(e) => setMotivo(e.target.value)}>
            {MOTIVOS_SAIDA.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-wider text-ink-muted">Medicamento</label>
        <div className="mt-1 flex gap-2">
          <MedicamentoCombobox
            medicamentos={medicamentos ?? []}
            value={medId}
            onChange={(id) => { setMedId(id); setLoteId(""); }}
            disabled={!localId}
            placeholder={localId ? "digite código ou nome…" : "escolha o local primeiro"}
          />
          <button
            type="button" className="btn-secondary" title="Escanear etiqueta interna ou EAN"
            onClick={() => setScannerOpen(true)} disabled={!localId}
          >📷</button>
        </div>
        {med?.portaria_344 ? (
          <p className="mt-1 text-xs text-amber-700">⚠️ Controlado (Portaria 344) — a baixa será escriturada no livro com a justificativa.</p>
        ) : null}
        {eanPendente ? (
          <VincularEan
            ean={eanPendente}
            med={med}
            onDone={(msg) => { setEanPendente(null); setScanMsg(msg); }}
          />
        ) : null}
        {scanMsg ? <p className="mt-1 text-xs text-anarosa">{scanMsg}</p> : null}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Lote (saldo no local)</label>
          <select
            className="input mt-1"
            value={loteId}
            onChange={(e) => setLoteId(e.target.value)}
            disabled={!localId || !medId}
          >
            <option value="">— escolha o lote —</option>
            {(lotes ?? []).map((l) => (
              <option key={l.lote_id} value={l.lote_id}>
                {l.numero_lote} · val. {fmtDate(l.data_validade)} · saldo {l.quantidade}
              </option>
            ))}
          </select>
          {medId && localId && lotes && lotes.length === 0 ? (
            <p className="mt-1 text-xs text-ink-muted">Sem lotes com saldo deste medicamento neste local.</p>
          ) : null}
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-ink-muted">Quantidade</label>
          <input
            className="input mt-1" type="number" min={1}
            max={lote?.quantidade}
            value={qtd} onChange={(e) => setQtd(e.target.value)}
          />
          {lote ? <p className="mt-1 text-xs text-ink-muted">Disponível: {lote.quantidade}</p> : null}
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-wider text-ink-muted">
          Justificativa <span className="text-anarosa">(obrigatória)</span>
        </label>
        <input
          className="input mt-1"
          placeholder="ex.: ampola quebrada na manipulação; lote vencido em 30/06, descartado conforme PGRSS"
          value={obs} onChange={(e) => setObs(e.target.value)}
        />
        {obsCurta ? <p className="mt-1 text-xs text-anarosa">Descreva a justificativa (mín. 5 caracteres).</p> : null}
      </div>

      <div className="flex items-center gap-3 pt-1">
        <button className="btn-primary" disabled={!podeSalvar} onClick={submit}>
          {baixar.isPending ? "Baixando…" : "Lançar baixa"}
        </button>
        {baixar.error ? <span className="text-anarosa text-sm">{(baixar.error as Error).message}</span> : null}
        {baixar.isSuccess ? <span className="text-green-700 text-sm">✅ Baixa lançada.</span> : null}
      </div>

      <Scanner open={scannerOpen} onScan={handleScan} onClose={() => setScannerOpen(false)} />
    </div>
  );
}
